import { useCallback, useEffect, useRef, useState } from "react";
import { useChartTheme, useDynamicPanes } from "../../lib/core";
import { LEGACY_COMPOSITE_KEY, normalizeChartId, readScopedSetting, removeScopedSetting, shouldAcceptSettingsEvent, writeScopedSetting } from "../components/settings/storageKeys";
import { hydrateAlertAudioEnabled, hydrateCvdMode, hydrateTimezone } from "../components/settings/settingsHydration";
import { useVNInvestChartsState, useShadowRoot } from "../context/VNInvestChartsContext";
import { useWidgetI18n } from "../context/WidgetI18nContext";
import SaveChartAsImage from "../../lib/helper/SaveChartAsImage";
export function useWidgetSettings({ locale, theme, timezone, defaultTimezone, onTimezoneChange, panes, maxVisiblePanes, maxVisibleBars, showNonTradingDays, showTimeScrollbar, showDrawingPriceMarkers, enableWhale, whaleThreshold, enableCVD, cvdMode, containerRef, size, tierRef, settingsOpen, onSettingsClose, timeframe, range, symbol, plotData, onTimeframeChange, onRangeChange, onPaneToggleVisibleRequest, onPaneRemoveRequest, onPaneAddSeriesRequest, onPaneToggleSeriesRequest, onPaneRemoveSeriesRequest, storageAdapter, syncOverrides, id, }) {
    const myChartId = normalizeChartId(id);
    const readSetting = (base) => readScopedSetting(storageAdapter, base, myChartId);
    const shadowRoot = useShadowRoot();
    const { t: rawT } = useWidgetI18n();
    const t = useCallback((key) => rawT(key) ?? key, [rawT]);
    const [previewImageDataUrl, setPreviewImageDataUrl] = useState(null);
    const [localTimeframe, setLocalTimeframe] = useState(timeframe || "1h");
    useEffect(() => {
        if (timeframe) {
            if (syncOverrides?.timeframe === false) {
                return;
            }
            setLocalTimeframe(timeframe);
        }
    }, [timeframe, syncOverrides?.timeframe]);
    const [localRange, setLocalRange] = useState(range);
    useEffect(() => {
        if (range) {
            setLocalRange(range);
        }
    }, [range]);
    const [internalXExtents, setInternalXExtents] = useState(undefined);
    const [localSelectedPaneId, setLocalSelectedPaneId] = useState("price");
    // Pinned (docked) drawing toolbar — shared persistence key with
    // VNInvestMultiCharts so the preference is global across modes.
    const [toolbarLocked, setToolbarLockedState] = useState(() => {
        try {
            return typeof localStorage !== "undefined" && localStorage.getItem("vnsc_toolbar_docked") === "true";
        }
        catch {
            return false;
        }
    });
    const setToolbarLocked = useCallback((action) => {
        setToolbarLockedState((prev) => {
            const next = typeof action === "function" ? action(prev) : action;
            try {
                if (typeof localStorage !== "undefined")
                    localStorage.setItem("vnsc_toolbar_docked", String(next));
            }
            catch { /* private-mode: session-only */ }
            return next;
        });
    }, []);
    const [localMaxVisiblePanes, setLocalMaxVisiblePanes] = useState(() => {
        const val = readSetting("vnsc_settings_maxVisiblePanes") || readSetting("vnsc_maxVisiblePanes");
        return val ? parseInt(val, 10) : 6;
    });
    const activeMaxVisiblePanes = maxVisiblePanes !== undefined ? maxVisiblePanes : localMaxVisiblePanes;
    const [localMaxVisibleBars, setLocalMaxVisibleBars] = useState(() => {
        const val = readSetting("vnsc_settings_maxVisibleBars") || readSetting("vnsc_maxVisibleBars");
        return val ? parseInt(val, 10) : 40000;
    });
    const activeMaxVisibleBars = maxVisibleBars !== undefined ? maxVisibleBars : localMaxVisibleBars;
    const [localShowNonTradingDays, setLocalShowNonTradingDays] = useState(() => {
        const val = readSetting("vnsc_settings_showNonTradingDays") || readSetting("vnsc_showNonTradingDays");
        return val ? val === "true" : false;
    });
    const activeShowNonTradingDays = showNonTradingDays !== undefined ? showNonTradingDays : localShowNonTradingDays;
    const [localShowDrawingPriceMarkers, setLocalShowDrawingPriceMarkers] = useState(() => {
        const val = readSetting("vnsc_settings_showDrawingPriceMarkers") || readSetting("vnsc_showDrawingPriceMarkers");
        return val ? val === "true" : true;
    });
    const activeShowDrawingPriceMarkers = showDrawingPriceMarkers !== undefined ? showDrawingPriceMarkers : localShowDrawingPriceMarkers;
    const [overlayTimeframe, setOverlayTimeframe] = useState(() => {
        return readSetting("vnsc_settings_overlayTimeframe") || readSetting("vnsc_overlayTimeframe") || undefined;
    });
    const [overlayStyle, setOverlayStyle] = useState(() => {
        return readSetting("vnsc_settings_overlayStyle") || readSetting("vnsc_overlayStyle") || "candle"; // ARC-1-keep: legacy bridge
    });
    useEffect(() => {
        try {
            if (overlayTimeframe) {
                writeScopedSetting(storageAdapter, "vnsc_settings_overlayTimeframe", overlayTimeframe, myChartId);
                writeScopedSetting(storageAdapter, "vnsc_overlayTimeframe", overlayTimeframe, myChartId);
            }
            else {
                removeScopedSetting(storageAdapter, "vnsc_settings_overlayTimeframe", myChartId);
                removeScopedSetting(storageAdapter, "vnsc_overlayTimeframe", myChartId);
            }
        }
        catch { }
    }, [overlayTimeframe, myChartId]);
    useEffect(() => {
        writeScopedSetting(storageAdapter, "vnsc_settings_overlayStyle", overlayStyle, myChartId);
        writeScopedSetting(storageAdapter, "vnsc_overlayStyle", overlayStyle, myChartId);
    }, [overlayStyle, myChartId]);
    const [localEnableWhale, setLocalEnableWhale] = useState(() => {
        const val = readSetting("vnsc_settings_enableWhale") || readSetting("vnsc_enableWhale");
        return val ? val === "true" : true;
    });
    const activeEnableWhale = enableWhale !== undefined ? enableWhale : localEnableWhale;
    const [localWhaleThreshold, setLocalWhaleThreshold] = useState(() => {
        const val = readSetting("vnsc_settings_whaleThreshold") || readSetting("vnsc_whaleThreshold");
        return val ? parseInt(val, 10) : 1000000;
    });
    const activeWhaleThreshold = whaleThreshold ?? localWhaleThreshold;
    const [localEnableCVD, setLocalEnableCVD] = useState(() => {
        const val = readSetting("vnsc_settings_enableCVD") || readSetting("vnsc_enableCVD");
        return val ? val === "true" : false;
    });
    const activeEnableCVD = enableCVD !== undefined ? enableCVD : localEnableCVD;
    const [localCvdMode, setLocalCvdMode] = useState(() => hydrateCvdMode(readSetting));
    const activeCvdMode = cvdMode !== undefined ? cvdMode : localCvdMode;
    const [localTimezone, setLocalTimezone] = useState(() => hydrateTimezone(readSetting));
    const activeTimezone = timezone || localTimezone;
    const [localAlertAudioEnabled, setLocalAlertAudioEnabled] = useState(() => hydrateAlertAudioEnabled(readSetting));
    const activeAlertAudioEnabled = localAlertAudioEnabled;
    const [localSettingsOpen, setLocalSettingsOpen] = useState(false);
    const activeSettingsOpen = settingsOpen !== undefined ? settingsOpen : localSettingsOpen;
    const themeState = useChartTheme("modern-light");
    const resolvedTheme = theme ?? themeState.theme;
    const isDark = resolvedTheme !== "modern-light" && resolvedTheme !== "neutral-pro";
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [statusLineSettings, setStatusLineSettings] = useState(() => {
        const raw = readSetting(LEGACY_COMPOSITE_KEY);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed.statusLine)
                    return { ...parsed.statusLine, showOHLC: parsed.statusLine.showOHLC ?? parsed.statusLine.showOhlc ?? true };
            }
            catch {
                // Corrupted JSON fallback
            }
        }
        return {
            showLogo: false,
            showTicker: true,
            showOHLC: true,
            showBarChange: true,
            showVolume: true,
            showIndicatorTitles: true,
            showIndicatorArguments: true,
            showIndicatorValues: true,
            backgroundOpacity: 0.8,
        };
    });
    const [scalesSettings, setScalesSettings] = useState(() => {
        const defaultShowScrollbar = showTimeScrollbar !== undefined ? showTimeScrollbar : !myChartId;
        const val = readSetting("vnsc_settings_showTimeScrollbar");
        const storedScrollbar = val !== null && val !== undefined ? val === "true" : undefined;
        const raw = readSetting(LEGACY_COMPOSITE_KEY);
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed.scales) {
                    return {
                        ...parsed.scales,
                        showTimeScrollbar: storedScrollbar !== undefined ? storedScrollbar : (parsed.scales.showTimeScrollbar ?? defaultShowScrollbar),
                        dateFormat: parsed.scales.dateFormat ?? "yyyy-mm-dd",
                    };
                }
            }
            catch {
                // Corrupted JSON fallback
            }
        }
        if (storedScrollbar !== undefined) {
            return {
                priceScaleMode: "normal",
                invertScale: false,
                countdownToBarClose: true,
                showTimeScrollbar: storedScrollbar,
                dateFormat: "yyyy-mm-dd",
            };
        }
        return {
            priceScaleMode: "normal",
            invertScale: false,
            countdownToBarClose: true,
            showTimeScrollbar: defaultShowScrollbar,
            dateFormat: "yyyy-mm-dd",
        };
    });
    useEffect(() => {
        const handleSync = (e) => {
            const custom = e;
            if (!shouldAcceptSettingsEvent(myChartId, custom.detail?.chartId ?? null))
                return;
            // Enveloped (detail.state) and legacy (detail directly) shapes.
            const state = custom.detail?.state ?? custom.detail;
            if (state?.statusLine)
                setStatusLineSettings({ ...state.statusLine, showOHLC: state.statusLine.showOHLC ?? state.statusLine.showOhlc ?? true });
            if (state?.scales) {
                setScalesSettings(state.scales);
            }
        };
        if (typeof window !== "undefined") {
            window.addEventListener("vnsc-settings-sync", handleSync);
            return () => window.removeEventListener("vnsc-settings-sync", handleSync);
        }
    }, [myChartId]);
    useEffect(() => {
        if (typeof document === "undefined")
            return;
        const handleFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };
        // Use shadowRoot for capture-phase listeners in Shadow DOM mode
        const target = shadowRoot ?? document;
        target.addEventListener("fullscreenchange", handleFullscreenChange, { capture: true });
        return () => {
            target.removeEventListener("fullscreenchange", handleFullscreenChange, { capture: true });
        };
    }, [containerRef, shadowRoot]);
    const handleFullscreenToggle = useCallback(() => {
        if (!containerRef.current)
            return;
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch((err) => {
                console.debug(`Error entering fullscreen: ${err.message}`);
            });
        }
        else {
            document.exitFullscreen();
        }
    }, [containerRef]);
    useEffect(() => {
        if (typeof document === "undefined")
            return;
        if ((activeSettingsOpen || !!previewImageDataUrl) && document.fullscreenElement) {
            document.exitFullscreen().catch((err) => {
                console.debug(`Error exiting fullscreen: ${err.message}`);
            });
        }
    }, [activeSettingsOpen, previewImageDataUrl]);
    // W1.4: KHÔNG gate ở đây. `useWidgetSettings` được tạo ở dòng ~288, **trước**
    // `useTierGatedFeatures` (~693) nên hook này không thấy tier. Gate nằm ở
    // `handleGatedSnapshot` trong `VNInvestCharts` — đặt sau `handleUpgradeFromBanner`
    // để dùng được cho cả nút header lẫn public API `saveAsImage`.
    const handleSnapshot = useCallback(() => {
        if (containerRef.current) {
            const bg = isDark ? "#0a0e14" : "#ffffff";
            SaveChartAsImage.save(document, containerRef.current, bg, isDark, (dataUrl) => {
                setPreviewImageDataUrl(dataUrl);
            }, { tier: tierRef?.current });
        }
    }, [containerRef, isDark]);
    const activeOnSettingsClose = useCallback(() => {
        if (onSettingsClose) {
            onSettingsClose();
        }
        else {
            setLocalSettingsOpen(false);
        }
    }, [onSettingsClose]);
    // Text edit state
    const [isEditingText, setIsEditingText] = useState(false);
    const [drawingTextDraft, setDrawingTextDraft] = useState("");
    const handleStartTextEdit = useCallback((draft) => {
        setDrawingTextDraft(draft);
        setIsEditingText(true);
    }, []);
    const handleCommitTextEdit = useCallback((commitCallback) => {
        commitCallback(drawingTextDraft);
        setIsEditingText(false);
    }, [drawingTextDraft]);
    const handleCancelTextEdit = useCallback(() => {
        setIsEditingText(false);
    }, []);
    // Consuming context paneState to synchronize settings/toolbar actions
    const { paneState } = useVNInvestChartsState();
    // Local panes state fallback
    const { panes: localPanes, visiblePanes: localVisiblePanes, heights: localHeights, available: localAvailable, toggleVisible: localToggleVisible, addPane: localAddPane, removePane: localRemovePane, addSeries: localAddSeries, removeSeries: localRemoveSeries, toggleSeriesVisible: localToggleSeriesVisible, applyDelta: localApplyDelta, equalizePair: localEqualizePair, replaceLayout: localReplaceLayout, resetToDefault: localResetToDefault, } = useDynamicPanes(size.height, {
        maxVisiblePanes: activeMaxVisiblePanes,
        panesOverride: paneState.panes,
        dispatchOverride: paneState.dispatch,
        storageScopeId: myChartId ?? null,
    });
    useEffect(() => {
        if (panes) {
            localReplaceLayout(panes);
        }
    }, [panes, localReplaceLayout]);
    const onTimeframeChangeRef = useRef(onTimeframeChange);
    onTimeframeChangeRef.current = onTimeframeChange;
    const onRangeChangeRef = useRef(onRangeChange);
    onRangeChangeRef.current = onRangeChange;
    const handleTimeframeChange = useCallback((tf) => {
        setLocalTimeframe(tf);
        onTimeframeChangeRef.current?.(tf);
    }, []);
    const handleRangeChange = useCallback((r) => {
        setLocalRange(r);
        onRangeChangeRef.current?.(r);
    }, []);
    // Series Display Translation Helper
    const getPaneLabel = useCallback((id, label) => {
        if (id === "price")
            return t("pane.price") || label;
        if (id === "volume")
            return t("pane.volume") || label;
        const val = t(`pane.${id}`);
        if (val == null || val === `pane.${id}`)
            return label;
        return val;
    }, [t]);
    const localizePane = useCallback((pane) => {
        const nextLabel = getPaneLabel(pane.id, pane.label);
        return nextLabel === pane.label ? pane : { ...pane, label: nextLabel };
    }, [getPaneLabel]);
    const handlePaneToggleVisible = useCallback((paneId) => {
        if (onPaneToggleVisibleRequest) {
            onPaneToggleVisibleRequest(paneId);
        }
        else {
            localToggleVisible(paneId);
        }
    }, [localToggleVisible, onPaneToggleVisibleRequest]);
    const handlePaneRemove = useCallback((paneId) => {
        if (onPaneRemoveRequest) {
            onPaneRemoveRequest(paneId);
        }
        else {
            localRemovePane(paneId);
        }
    }, [localRemovePane, onPaneRemoveRequest]);
    const handlePaneAddSeries = useCallback((paneId) => {
        if (onPaneAddSeriesRequest) {
            onPaneAddSeriesRequest(paneId);
        }
        else {
            setLocalSelectedPaneId(paneId);
            setLocalSettingsOpen(true);
        }
    }, [onPaneAddSeriesRequest]);
    // P3-a: legend gear button — open SettingsModal focused on the series' pane.
    const handlePaneEditSeries = useCallback((paneId) => {
        setLocalSelectedPaneId(paneId);
        setLocalSettingsOpen(true);
    }, []);
    const handlePaneToggleSeries = useCallback((paneId, seriesType, seriesIndex) => {
        if (onPaneToggleSeriesRequest) {
            onPaneToggleSeriesRequest(paneId, seriesType, seriesIndex);
        }
        else {
            localToggleSeriesVisible(paneId, seriesType, seriesIndex);
        }
    }, [localToggleSeriesVisible, onPaneToggleSeriesRequest]);
    const handlePaneRemoveSeries = useCallback((paneId, seriesType, seriesIndex) => {
        if (onPaneRemoveSeriesRequest) {
            onPaneRemoveSeriesRequest(paneId, seriesType, seriesIndex);
        }
        else {
            localRemoveSeries(paneId, seriesType, seriesIndex);
        }
    }, [localRemoveSeries, onPaneRemoveSeriesRequest]);
    const activeLocale = locale ?? "vi";
    const [magnetSensitivity, setMagnetSensitivity] = useState("normal");
    // The crosshair mode is owned by `useChartSettingsSections` (Batch B / B2):
    // the top bar and the appearance tab must never disagree, and the tab
    // writes the composite while the top bar writes the scoped key.
    return {
        localTimeframe,
        setLocalTimeframe,
        localRange,
        setLocalRange,
        internalXExtents,
        setInternalXExtents,
        localSelectedPaneId,
        setLocalSelectedPaneId,
        toolbarLocked,
        setToolbarLocked,
        localMaxVisiblePanes,
        setLocalMaxVisiblePanes,
        activeMaxVisiblePanes,
        localMaxVisibleBars,
        setLocalMaxVisibleBars,
        activeMaxVisibleBars,
        localShowNonTradingDays,
        setLocalShowNonTradingDays,
        activeShowNonTradingDays,
        localShowDrawingPriceMarkers,
        setLocalShowDrawingPriceMarkers,
        activeShowDrawingPriceMarkers,
        localEnableWhale,
        setLocalEnableWhale,
        activeEnableWhale,
        localWhaleThreshold,
        setLocalWhaleThreshold,
        activeWhaleThreshold,
        localEnableCVD,
        setLocalEnableCVD,
        activeEnableCVD,
        localCvdMode,
        setLocalCvdMode,
        activeCvdMode,
        localTimezone,
        setLocalTimezone,
        activeTimezone,
        localAlertAudioEnabled,
        setLocalAlertAudioEnabled,
        activeAlertAudioEnabled,
        localSettingsOpen,
        setLocalSettingsOpen,
        activeSettingsOpen,
        activeOnSettingsClose,
        isFullscreen,
        setIsFullscreen,
        searchOpen,
        setSearchOpen,
        isEditingText,
        setIsEditingText,
        drawingTextDraft,
        setDrawingTextDraft,
        handleStartTextEdit,
        handleCommitTextEdit,
        handleCancelTextEdit,
        handleFullscreenToggle,
        handleSnapshot,
        handleTimeframeChange,
        handleRangeChange,
        getPaneLabel,
        localizePane,
        handlePaneToggleVisible,
        handlePaneRemove,
        handlePaneAddSeries,
        handlePaneEditSeries,
        handlePaneToggleSeries,
        handlePaneRemoveSeries,
        visiblePanes: localVisiblePanes,
        activeHeights: localHeights,
        themeState,
        resolvedTheme,
        isDark,
        previewImageDataUrl,
        setPreviewImageDataUrl,
        activeLocale,
        magnetSensitivity,
        setMagnetSensitivity,
        localAvailable,
        localApplyDelta,
        localEqualizePair,
        localResetToDefault,
        t: (key) => t(key) ?? key,
        overlayTimeframe,
        setOverlayTimeframe,
        overlayStyle,
        setOverlayStyle,
        statusLineSettings,
        scalesSettings,
    };
}
